import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Send, Loader2, Bot, User, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function sid() {
  let s = sessionStorage.getItem("momento_chat_sid");
  if (!s) { s = "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("momento_chat_sid", s); }
  return s;
}

export default function ChatWidget() {
  const { subId } = useParams();
  const [cfg, setCfg] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [ho, setHo] = useState({ name: "", email: "" });
  const [handedOff, setHandedOff] = useState(false);
  const scrollRef = useRef(null);
  const session = sid();

  useEffect(() => {
    axios.get(`${API}/public/chat/${subId}`).then(({ data }) => {
      setCfg(data);
      if (data.enabled) setMsgs([{ role: "assistant", text: data.greeting }]);
    }).catch(() => setCfg(false));
  }, [subId]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/chat/${subId}/message`, { session_id: session, message: text });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch { setMsgs((m) => [...m, { role: "assistant", text: "Sorry, something went wrong. Please try again." }]); }
    finally { setBusy(false); }
  };

  const sendHandoff = async () => {
    try {
      await axios.post(`${API}/public/chat/${subId}/handoff`, { session_id: session, name: ho.name, email: ho.email, message: "Visitor requested a human." });
      setHandedOff(true); setShowHandoff(false);
    } catch { setHandedOff(true); setShowHandoff(false); }
  };

  if (cfg === null) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (cfg === false || !cfg.enabled) return <div className="min-h-screen flex items-center justify-center bg-white text-slate-400 text-sm" data-testid="chat-disabled">Chat is not available.</div>;

  const color = cfg.color || "#6366F1";

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="chat-widget">
      <div className="px-4 py-3 flex items-center gap-3 text-white" style={{ background: color }}>
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Bot className="w-5 h-5" /></div>
        <div><p className="font-semibold text-sm">{cfg.brand?.name || "Chat"}</p><p className="text-[11px] opacity-80">We typically reply in a few minutes</p></div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {msgs.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
            {m.role === "assistant" && <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-white" style={{ background: color }}><Bot className="w-4 h-4" /></div>}
            <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "text-white" : "bg-white border border-slate-200 text-slate-800"}`} style={m.role === "user" ? { background: color } : {}}>{m.text}</div>
            {m.role === "user" && <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0"><User className="w-4 h-4 text-slate-500" /></div>}
          </div>
        ))}
        {busy && <div className="flex gap-2"><div className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{ background: color }}><Bot className="w-4 h-4" /></div><div className="rounded-2xl px-3.5 py-2 bg-white border border-slate-200"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div></div>}
        {handedOff && <div className="flex items-center gap-2 justify-center text-xs text-emerald-600 py-2" data-testid="handoff-confirmed"><CheckCircle2 className="w-4 h-4" />Thanks! The team will follow up by email shortly.</div>}
      </div>

      {showHandoff ? (
        <div className="p-4 border-t border-slate-200 space-y-2 bg-white" data-testid="handoff-form">
          <p className="text-sm font-medium text-slate-700">Leave your details and we'll get back to you:</p>
          <input value={ho.name} onChange={(e) => setHo({ ...ho, name: e.target.value })} placeholder="Your name" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none" data-testid="handoff-name" />
          <input value={ho.email} onChange={(e) => setHo({ ...ho, email: e.target.value })} placeholder="Your email" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none" data-testid="handoff-email" />
          <button onClick={sendHandoff} disabled={!ho.email} className="w-full rounded-lg py-2.5 text-sm font-semibold text-white" style={{ background: color }} data-testid="handoff-submit">Send to the team</button>
        </div>
      ) : (
        <form onSubmit={send} className="p-3 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a message…" className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400" data-testid="chat-input" />
            <button type="submit" disabled={busy || !input.trim()} className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: color }} data-testid="chat-send"><Send className="w-4 h-4" /></button>
          </div>
          <button type="button" onClick={() => setShowHandoff(true)} className="text-[11px] text-slate-400 hover:text-slate-600 mt-2" data-testid="talk-to-human">Talk to a human →</button>
        </form>
      )}
    </div>
  );
}
